<template>
    <section>
        <div class="card-tabs" v-bind:class="{ 'card-tabs--overflowing': tabsOverflow }">
            <nav class="card-title-tabs" role="tablist" ref="tabsNav">
                <!-- One raised pill glides between tabs; the tabs themselves sit on top of it: -->
                <span class="card-title-tab__indicator" v-bind:style="indicatorStyle"/>

                <span class="card-title-tab" role="tab" tabindex="0"
                    v-for="tab in tabs"
                    v-bind:key="tab.key"
                    ref="tabEls"
                    v-bind:data-tab-key="tab.key"
                    v-on:click="activeTab = tab.key"
                    v-on:keyup.enter="activeTab = tab.key"
                    v-bind:id="`tab_switcher_${tab.key}`"
                    v-bind:aria-selected="activeTab === tab.key"
                    v-bind:aria-controls="`tab_pane_${tab.key}`"
                    v-bind:class="{
                        'card-title-tab--active': activeTab === tab.key,
                        'card-title-tab--disabled': tab.disabled,
                    }">
                    <component v-once v-bind:is="tab.icon" class="card-title-tab__icon"/>
                    {{tab.text}}
                    <span v-if="tab?.counter" class="card-title-tab__counter">
                        {{tab.counter}}
                    </span>
                </span>
            </nav>
            <div class="tab-buttons">
                <slot name="customCalendar"></slot>
                <slot name="customFilter"></slot>
            </div>
        </div>

        <div class="tab-pane" role="tabpanel"
            v-for="tab in tabs"
            v-bind:key="`ui_tab_pane_${tab.key}`"
            v-bind:aria-labelledby="`tab_switcher_${tab.key}`"
            v-bind:id="`tab_pane_${tab.key}`"
            v-show="activeTab === tab.key">
            <keep-alive max="2" exclude="tx-root">
                <component
                    v-if="!tab.disabled && (activeTab === tab.key || tab.content.eager)"
                    v-bind:key="`ui_tab_${tab.content.key}_${tab.key}`"
                    v-bind:is="tab.content.component"
                    v-bind="tab.content.props"
                    v-on="$listeners"/> <!-- passthru all listeners to parent -->
            </keep-alive>
        </div>
    </section>
</template>

<script>
export default {
    props: {
        tabs: {
            type: Array,
            required: true,
            default: () => [],
        },
        changeUrlHash: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            activeTab: undefined,
            indicatorStyle: { opacity: 0 },
            indicatorReady: false,
            tabsOverflow: false,
            resizeObserver: undefined,
        };
    },

    watch: {
        // A counter arriving later changes the tab width:
        tabs() {
            this.updateIndicator();
        },

        $route: {
            immediate: true,
            handler({ hash }) {
                // A hash that names one of the tabs opens it. Any other hash on
                // the page (the tools page keeps the active tool there) is none
                // of the tabs' business and leaves the current one in place.
                const key = (hash || '').replace('#', '');
                const isTabKey = this.tabs.some(tab => tab.key === key);
                this.activeTab = isTabKey ? key : (this.activeTab || this.tabs[0].key);
            },
        },

        activeTab(tabName) {
            if (this.changeUrlHash) {
                // don't trigger route watchers on hash change:
                window.history.replaceState(null, null, `${this.$route.path}#${tabName}`);
                this.updateFilterShow(tabName);
            }
            this.updateIndicator();
        },
    },

    mounted() {
        this.updateFilterShow(this.activeTab);
        this.updateIndicator();
        window.addEventListener('resize', this.updateIndicator);

        // Tab widths move when the web font lands or when an icon hides on a
        // narrower viewport; measure again whenever the tab boxes change:
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updateIndicator());
            this.resizeObserver.observe(this.$refs.tabsNav);
            (this.$refs.tabEls || []).forEach(el => el && this.resizeObserver.observe(el));
        } else if (document.fonts?.ready) {
            document.fonts.ready.then(this.updateIndicator);
        }
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.updateIndicator);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    methods: {
        /**
         * Slides the pill under the active tab. offsetLeft is relative to the nav,
         * so the value stays right when the tab strip is scrolled sideways on a
         * phone. The first placement is applied without a transition, otherwise the
         * pill would visibly fly in from the left corner on page load.
         */
        updateIndicator() {
            this.$nextTick(() => {
                const nav = this.$refs.tabsNav;
                const tabEls = this.$refs.tabEls;
                if (!nav || !tabEls) return;

                // The fade next to the buttons only makes sense when tabs actually scroll under it:
                this.tabsOverflow = nav.scrollWidth > nav.clientWidth + 1;

                const activeEl = (Array.isArray(tabEls) ? tabEls : [tabEls])
                    .find(el => el && el.dataset.tabKey === this.activeTab);

                if (!activeEl) {
                    this.indicatorStyle = { opacity: 0 };
                    return;
                }

                this.indicatorStyle = {
                    opacity: 1,
                    transform: `translateX(${activeEl.offsetLeft}px)`,
                    width: `${activeEl.offsetWidth}px`,
                    transition: this.indicatorReady ? undefined : 'none',
                };

                if (!this.indicatorReady) {
                    requestAnimationFrame(() => {
                        this.indicatorReady = true;
                    });
                }
            });
        },

        updateFilterShow(tabName) {
            this.$store.commit('updateNftFilterShow', tabName === 'nfts');

            if (tabName === 'events') {
                this.$store.commit('updateFilterShow', true);
                this.$store.commit('updateDyorLinkShow', false);
                this.$store.commit('updateNftItemCountShow', false);
            } else if (tabName === 'swaps') {
                this.$store.commit('updateFilterShow', false);
                this.$store.commit('updateDyorLinkShow', true);
                this.$store.commit('updateNftItemCountShow', false);
            } else if (tabName === 'items') {
                this.$store.commit('updateFilterShow', false);
                this.$store.commit('updateDyorLinkShow', false);
                this.$store.commit('updateNftItemCountShow', true);
            } else {
                this.$store.commit('updateFilterShow', false);
                this.$store.commit('updateDyorLinkShow', false);
                this.$store.commit('updateNftItemCountShow', false);
            }
        },
    },
};
</script>

<style lang="scss">
.card-tabs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--card-row-separator);
}

.card-title-tabs {
    position: relative;
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
    flex-wrap: nowrap;
    flex-direction: row;
    padding: 1px 0; // room for the pill's shadow
    margin: -1px 0;
    &::-webkit-scrollbar {
        display: none;
    }
}

.card-title-tab {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 14px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--card-header-tab-color);
    white-space: nowrap;
    user-select: none;
    cursor: pointer;
    transition: color .15s ease, transform .1s ease;

    &__icon {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        stroke: currentColor;
        fill: none;
        opacity: .85;
        transition: stroke .15s ease, opacity .15s ease;
    }

    &__counter {
        box-sizing: border-box;
        min-width: 18px;
        padding: 0 6px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        line-height: 18px;
        text-align: center;
        font-variant-numeric: tabular-nums;
        background-color: var(--body-light-muted-color);
        color: var(--card-header-tab-color);
        transition: background-color .15s ease, color .15s ease;
    }

    &:hover {
        color: var(--body-text-color);
    }
    &:active {
        transform: scale(.97);
    }
    &:focus-visible {
        outline: 2px solid var(--blue-bright);
        outline-offset: -2px;
    }

    &--active {
        &, &:hover {
            color: var(--body-text-color);
        }
        .card-title-tab__icon {
            stroke: var(--blue-bright);
            opacity: 1;
        }
        .card-title-tab__counter {
            background-color: var(--blue-bright-background);
            color: var(--blue-bright);
        }
    }

    &--disabled {
        pointer-events: none;
        opacity: .4;
    }
}

.card-title-tab__indicator {
    position: absolute;
    top: 1px;
    left: 0;
    z-index: 0;
    height: 36px;
    border-radius: 10px;
    background: var(--tab-thumb-background);
    box-shadow: var(--tab-thumb-shadow);
    pointer-events: none;
    transition: transform .32s cubic-bezier(.22, 1, .36, 1),
                width .32s cubic-bezier(.22, 1, .36, 1),
                opacity .2s ease;
}

@media (prefers-reduced-motion: reduce) {
    .card-title-tab__indicator,
    .card-title-tab {
        transition: none;
    }
}

@media all and (max-width: 480px) {
    .card-tabs {
        gap: 4px;
        padding: 8px 6px;
    }
    .card-title-tabs {
        gap: 0;
    }
    .card-title-tab {
        height: 34px;
        padding: 0 10px;
        font-size: 13.5px;
        &__icon {
            display: none;
        }
        &__counter {
            font-size: 10.5px;
            line-height: 17px;
            padding: 0 5px;
        }
    }
    .card-title-tab__indicator {
        height: 34px;
    }
}

.tab-buttons {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    position: relative;
    z-index: 1020;
}

// Tabs that scroll away under the buttons fade out instead of getting cut:
.card-tabs--overflowing .tab-buttons:before {
    content: "";
    height: 100%;
    width: 48px;
    position: absolute;
    left: -48px;
    top: 0;
    background: linear-gradient(to left, var(--card-header-background), transparent);
    pointer-events: none;
}
</style>
